/**
 * Check if a string exactly matches any string in an array (case-insensitive)
 */
export function isAnyExactMatch(target: string, candidates: string[]): boolean {
    if (!candidates.length) {
        return false
    }
    const targetLower = target.toLowerCase()
    return candidates.some(
        (candidate) => candidate.toLowerCase() === targetLower,
    )
}

/**
 * Calculate similarity between two strings using Levenshtein distance
 * Returns a score between 0-1, where 1 is perfect match
 */
export function calculateStringSimilarity(
    str1: string,
    str2: string,
    opts: {
        strategy?: 'levenshtein'
    } = {
        strategy: 'levenshtein',
    },
): number {
    const longer = str1.length > str2.length ? str1 : str2
    const shorter = str1.length > str2.length ? str2 : str1

    if (longer.length === 0) {
        return 1.0
    }
    if (opts.strategy !== 'levenshtein') {
        throw new Error('Invalid strategy')
    }
    const distance = calculateLevenshteinDistance(longer, shorter)
    return (longer.length - distance) / longer.length
}

function calculateLevenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = []

    for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
            if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1]
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1, // insertion
                    matrix[i - 1][j] + 1, // deletion
                )
            }
        }
    }

    return matrix[str2.length][str1.length]
}
